import { useEffect, useState } from "react";
import axios from "axios";
import { useTaal } from "@/contexts/TaalContext";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * Van de laatste vijftig besluiten: hoeveel nam je ongewijzigd over.
 *
 * **TREDE 2, MASTERPLAN 3ax-4.** Franks meetpunt, en zijn voorwaarde om naar
 * trede 3 te gaan: *"Herschrijft hij de helft, dan is het model niet klaar om
 * ze uit te voeren."* Dit blok is waar dat getal te zien is.
 *
 * **HET AANTAL HERSCHREVEN STAAT ER APART, naast het percentage.** Alleen een
 * percentage tonen laat "hij herschrijft de helft" lezen als "zeventig procent
 * ging goed", en dat is precies de kant die deze meting moet uitsluiten.
 *
 * **ZONDER BESLUITEN STAAT ER GEEN PERCENTAGE.** Nul van nul is geen honderd
 * procent. Een blok dat op een lege dag "100% ongewijzigd" meldt, is de
 * geruststelling waar de meting tegen beschermt.
 *
 * **EN EEN SERVER DIE NIET ANTWOORDT LEVERT GEEN NUL MAAR NIETS.** Een nul die
 * eigenlijk "onbekend" betekent, is een leugen met een cijfer erin - dezelfde
 * vorm als de lege grep die als bevinding werd gelezen.
 */
export default function VoorstelTelling({ prikkel = 0 }) {
  const { t } = useTaal();
  const [telling, setTelling] = useState(null);

  useEffect(() => {
    // `levend` en niet een AbortController: het gaat er niet om dat het verzoek
    // stopt maar dat een LAAT antwoord niet meer in een verdwenen component
    // landt. Bij een snelle tweede prikkel is dat precies wat er anders gebeurt.
    let levend = true;
    axios
      .get(`${API}/memory/candidates/telling`)
      .then(({ data }) => { if (levend) setTelling(data); })
      // Stil, zoals de noodstop: een foutmelding bij elke paginalading omdat
      // de dienst even niet antwoordt, leert niemand iets.
      .catch(() => {});
    return () => { levend = false; };
    // **`prikkel` EN NIETS ANDERS IN DE AFHANKELIJKHEDEN.** Een lege lijst haalt
    // het getal alleen bij het openen van de pagina op, en dan staat het blok
    // stil terwijl je zit te beslissen - een teller die niet meebeweegt leest
    // als een teller die stuk is. Een lijst die bij ELKE hertekening ophaalt is
    // het andere uiterste en bevraagt de server bij iedere toetsaanslag elders
    // op de pagina. Een getal dat de aanroeper ophoogt NA een geslaagd besluit,
    // is precies zo vaak als er iets te halen valt.
  }, [prikkel]);

  if (!telling) return null;

  // `== null` en niet `=== null`: een ONTBREKEND veld is net zo goed "geen
  // getal" als een uitdrukkelijke null. Met de strikte vergelijking levert een
  // antwoord zonder dit veld `Math.round(undefined * 100)` op, en dan staat er
  // NaN% op het scherm - een getal dat niets betekent leest als een getal dat
  // wel iets betekent.
  const percentage = telling.aandeel_ongewijzigd == null
    ? null
    : Math.round(telling.aandeel_ongewijzigd * 100);

  return (
    <div
      className="mb-4 max-w-prose rounded-lg border p-3"
      data-testid="telling-blok"
      aria-label={t("Hoe de voorstellen gingen")}
    >
      <h2 className="text-sm font-medium">{t("Hoe de voorstellen gingen")}</h2>
      {percentage === null ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {t("Nog niets besloten. Dit begint te tellen zodra je iets aanneemt of weigert.")}
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm">
            <span data-testid="telling-aandeel" className="font-medium">
              {percentage}%
            </span>
            {" "}{t("ongewijzigd overgenomen, van de laatste {n}.", { n: telling.bekeken })}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span data-testid="telling-gewijzigd">{telling.gewijzigd}</span>
            {" "}{t("herschreven voor het aannemen")} ·{" "}
            <span data-testid="telling-afgewezen">{telling.afgewezen}</span> {t("geweigerd")}
          </p>
        </>
      )}
    </div>
  );
}
